import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Loader2, ArrowRight, Sparkles } from "lucide-react";
import { predictiveSearch, formatShopifyPrice } from "../lib/shopify";

const FALLBACK_IMG =
    "https://images.pexels.com/photos/163036/mario-luigi-yoschi-figures-163036.jpeg?auto=compress&cs=tinysrgb&w=200";

const HINTS = ["Luffy", "Zoro", "Ace", "Shanks", "Kaido", "Law"];

export default function SearchModal({ open, onClose }) {
    const [term, setTerm] = useState("");
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState({ products: [], queries: [] });
    const inputRef = useRef(null);
    const navigate = useNavigate();
    const debounceRef = useRef(null);

    // Focus input when opened
    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 100);
        } else {
            setTerm("");
            setResults({ products: [], queries: [] });
        }
    }, [open]);

    // Escape to close
    useEffect(() => {
        function handleKey(e) {
            if (e.key === "Escape" && open) onClose();
        }
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [open, onClose]);

    // Debounced search
    useEffect(() => {
        clearTimeout(debounceRef.current);
        if (!term || term.trim().length < 2) {
            setResults({ products: [], queries: [] });
            setLoading(false);
            return;
        }
        setLoading(true);
        debounceRef.current = setTimeout(async () => {
            const data = await predictiveSearch(term, 8);
            setResults(data);
            setLoading(false);
        }, 250);
        return () => clearTimeout(debounceRef.current);
    }, [term]);

    function goToProduct(handle) {
        navigate(`/figura/${handle}`);
        onClose();
    }

    function goToShopWithQuery(q) {
        navigate(`/figure?q=${encodeURIComponent(q)}`);
        onClose();
    }

    function submitTop(e) {
        e.preventDefault();
        if (results.products.length > 0) {
            goToProduct(results.products[0].handle);
        } else if (term.trim().length >= 2) {
            goToShopWithQuery(term.trim());
        }
    }

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-ink/70 z-[60]"
                        onClick={onClose}
                    />
                    <motion.div
                        initial={{ y: -30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -30, opacity: 0 }}
                        transition={{ type: "spring", damping: 22 }}
                        className="fixed top-4 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] sm:w-[560px] max-w-2xl z-[70] bg-parchment border-2 border-ink shadow-manga-lg"
                        data-testid="search-modal"
                    >
                        <form
                            onSubmit={submitTop}
                            className="flex items-center gap-3 p-4 border-b-2 border-ink bg-ink text-parchment"
                        >
                            <Search size={20} />
                            <input
                                ref={inputRef}
                                type="text"
                                value={term}
                                onChange={(e) => setTerm(e.target.value)}
                                placeholder="Cerca un pirata (Luffy, Zoro, Ace...)"
                                className="flex-1 bg-transparent text-parchment placeholder:text-parchment/50 outline-none font-mono text-sm tracking-wider"
                                data-testid="search-input"
                                autoComplete="off"
                            />
                            {loading && (
                                <Loader2 size={18} className="animate-spin" />
                            )}
                            <button
                                type="button"
                                onClick={onClose}
                                aria-label="Chiudi"
                                className="w-8 h-8 border-2 border-parchment flex items-center justify-center hover:bg-bounty"
                                data-testid="search-close-btn"
                            >
                                <X size={14} />
                            </button>
                        </form>

                        <div className="max-h-[70vh] overflow-y-auto">
                            {term.trim().length < 2 && (
                                <div className="p-6">
                                    <div className="font-hand text-lg text-bounty mb-3 dashed-underline inline-block">
                                        prova a cercare
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {HINTS.map((h) => (
                                            <button
                                                key={h}
                                                onClick={() => setTerm(h)}
                                                className="px-3 py-1.5 border-2 border-ink bg-white font-mono text-xs uppercase tracking-widest hover:bg-bounty hover:text-parchment transition-manga"
                                                data-testid={`search-hint-${h}`}
                                            >
                                                {h}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="mt-6 flex items-center gap-2 text-xs font-mono text-ink/60">
                                        <Sparkles size={14} />
                                        Ricerca live via Shopify · digita almeno
                                        2 lettere
                                    </div>
                                </div>
                            )}

                            {term.trim().length >= 2 &&
                                !loading &&
                                results.products.length === 0 &&
                                results.queries.length === 0 && (
                                    <div className="p-8 text-center">
                                        <div className="font-hand text-2xl text-bounty">
                                            Nessuna rotta trovata
                                        </div>
                                        <p className="text-sm text-ink/60 mt-1">
                                            Prova un altro nome o esplora
                                            l&apos;intero bottino.
                                        </p>
                                        <button
                                            onClick={() =>
                                                goToShopWithQuery(term)
                                            }
                                            className="btn-manga btn-manga-red mt-4"
                                            data-testid="search-empty-view-all"
                                        >
                                            Vedi tutte le figure
                                        </button>
                                    </div>
                                )}

                            {results.products.length > 0 && (
                                <div className="p-3">
                                    <div className="font-mono text-[10px] tracking-widest uppercase text-ink/50 px-2 mb-1">
                                        Prodotti
                                    </div>
                                    {results.products.map((p) => {
                                        const img =
                                            p.featuredImage?.url ||
                                            FALLBACK_IMG;
                                        return (
                                            <button
                                                key={p.id}
                                                onClick={() =>
                                                    goToProduct(p.handle)
                                                }
                                                className="w-full flex items-center gap-3 p-2 hover:bg-seafoam transition-manga text-left group"
                                                data-testid={`search-result-${p.handle}`}
                                            >
                                                <img
                                                    src={img}
                                                    alt={p.title}
                                                    className="w-14 h-14 object-cover border-2 border-ink"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-display text-lg leading-tight truncate">
                                                        {p.title}
                                                    </div>
                                                    <div className="font-mono text-xs text-ink/60 truncate">
                                                        {p.productType ||
                                                            p.vendor ||
                                                            "One Piece"}
                                                        {!p.availableForSale && (
                                                            <span className="ml-2 text-bounty font-bold">
                                                                · SOLD OUT
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="font-mono text-sm text-bounty font-bold">
                                                        {formatShopifyPrice(
                                                            p.priceRange
                                                                .minVariantPrice
                                                                .amount,
                                                            p.priceRange
                                                                .minVariantPrice
                                                                .currencyCode
                                                        )}
                                                    </div>
                                                </div>
                                                <ArrowRight
                                                    size={16}
                                                    className="opacity-0 group-hover:opacity-100 transition-manga"
                                                />
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {results.queries.length > 0 && (
                                <div className="p-3 border-t-2 border-ink">
                                    <div className="font-mono text-[10px] tracking-widest uppercase text-ink/50 px-2 mb-1">
                                        Ricerche suggerite
                                    </div>
                                    {results.queries.map((q) => (
                                        <button
                                            key={q}
                                            onClick={() =>
                                                goToShopWithQuery(q)
                                            }
                                            className="w-full flex items-center gap-2 p-2 hover:bg-seafoam text-left font-hand text-lg"
                                            data-testid={`search-query-${q.replace(/\s+/g, "-")}`}
                                        >
                                            <Search
                                                size={14}
                                                className="text-ink/50"
                                            />
                                            <span className="flex-1">{q}</span>
                                            <ArrowRight
                                                size={14}
                                                className="text-ink/50"
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="border-t-2 border-ink px-4 py-2 flex items-center justify-between font-mono text-[10px] tracking-widest text-ink/50">
                            <span>ESC per chiudere</span>
                            <span>ENTER per il primo risultato</span>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
